import React, { useState, useRef } from "react";
import dayjs from "dayjs";
import {
    Table,
    Spin,
    DatePicker,
} from "antd";
import { message } from "../../components/antdMessageBridge";
import CommonObj from "../../CommonObj";
import CalendarIcon from "../../assets/img/svg/xCalendaricon.svg?react";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import "./reports.css";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import "../../components/commonpagination/common-pagination.css";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import { CSVLink } from "react-csv";
import useQueueColumns from "../../components/commontable/useQueueColumns";
import { exportToCSV } from "../../components/exportToCsv";

const BillingReport = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchFilters, setsearchFilters] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const { accessToken } = useAuthToken();
    const { tableColumns, columnsLoading } = useQueueColumns("BillingReport");

    const [filteredData, setFilteredData] = useState([]);
    const [csvFileName, setCsvFileName] = useState("BillingReport.csv");
    const csvLinkRef = useRef(null);

    const [errors, setErrors] = useState({});

    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);

    const [startfloatLabel, setStartfloatLabel] = useState('');
    const [endfloatLabel, setEndfloatLabel] = useState('');


    const disableFutureDates = (current) => {
        const today = new Date();
        return current && current > today;
    };

    const validate = () => {
        const newErrors = {};

        if (!selectedStartDate) {
            newErrors.startdate = "Please select start date.";
        }
        if (!selectedEndDate) {
            newErrors.enddate = "Please select end date.";
        }
        if (selectedStartDate && selectedEndDate && new Date(selectedEndDate) < new Date(selectedStartDate)) {
            newErrors.datevalidate = "End Date cannot be lesser than Start Date";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCleardate = () => {
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setStartfloatLabel('');
        setEndfloatLabel('');

        setData([]);
        setFilteredData([]);
        setPagination((prev) => ({
            ...prev,
            current: 1,
            total: 0,
        }));

        setErrors({});
    };

    const handleStartDateChange = (date) => {
        setSelectedStartDate(date);

        if (date !== null) {
            setStartfloatLabel('float-label');
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.startdate;
                return newErrors;
            });
        } else {
            setStartfloatLabel('');
        }

    };

    const handleEndDateChange = (date) => {
        setSelectedEndDate(date);
        if (date !== null) {
            setEndfloatLabel('float-label');
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.enddate;
                return newErrors;
            });
        } else {
            setEndfloatLabel('');
        }
    };



    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const prepareCsvData = (rows) => {
        return rows.map((row) => ({
            ...row,
            // Prefix with a tab so Excel keeps ISBN as text and avoids scientific notation.
            xmlISBN: row?.xmlISBN != null ? `\t${row.xmlISBN}` : "",
        }));
    };

    const activeColumns = tableColumns;

    const buildCstFileName = () => {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Chicago",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        }).formatToParts(new Date());

        const valueByType = parts.reduce((acc, part) => {
            if (part.type !== "literal") {
                acc[part.type] = part.value;
            }
            return acc;
        }, {});

        return `BillingReport_${valueByType.year}${valueByType.month}${valueByType.day}_${valueByType.hour}${valueByType.minute}${valueByType.second}.csv`;
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        // Only update pagination state — no API call (client-side pagination)
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        // Fetch using the new pagination
        fetchData({
            pagination: {
                current: newPagination.current,
                pageSize: newPagination.pageSize,
            },
            sorter,
            filters: normalizeFilters,
        });
    };

    const handleExport_new = async () => {
        if (!validate()) {
            console.log("Validation Failed:", errors);
            return;
        }
        const startdate = selectedStartDate ? selectedStartDate.format('YYYY-MM-DD') : null;
        const enddate = selectedEndDate ? selectedEndDate.format('YYYY-MM-DD') : null;

        const payload = {
            startDate: startdate,
            endDate: enddate,
            pageNo: 1,
            pageSize: -1,
            search: "",
            orderByColumn: sortedInfo?.field || "ScanDate",
            orderBy: sortedInfo?.order === "ascend" ? "ASC" : "DESC",
        };

        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetBillingReport, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error("Network response was not ok");



            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
                result = responseText.includes("No billing report data found.") ? [] : result;
            } catch (e) {
                if (responseText.includes("No billing report data found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }

            const formattedData = Array.isArray(result) ? result : [];
            if (!formattedData.length) {
                message.error("The grid is currently empty. There are no records to export.");
                setData([]);
                setPagination((prev) => ({
                    ...prev,
                    current: 1,
                    total: 0,
                }));
                setFilteredData([]);
                return;
            }
            const processedData = prepareCsvData(formattedData);
            const filename = buildCstFileName();
            // Pass the data, your active columns, and the filename
            exportToCSV(processedData, activeColumns, filename);

        } catch (err) {
            message.error("Failed to export data. Please try again.");
        } finally {
            setLoading(false);
        }

    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {

        const startdate = selectedStartDate ? selectedStartDate.format('YYYY-MM-DD') : null;
        const enddate = selectedEndDate ? selectedEndDate.format('YYYY-MM-DD') : null;

        const payload = {
            startDate: startdate,
            endDate: enddate,
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: "",
            orderByColumn: sorter?.field || "ScanDate",
            orderBy: sorter?.order === "ascend" ? "ASC" : "DESC",
        };

        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetBillingReport, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error("Network response was not ok");

            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
                result = responseText.includes("No billing report data found.") ? [] : result;
            } catch (e) {
                if (responseText.includes("No billing report data found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }
            const formattedData = Array.isArray(result)
                ? result
                : Array.isArray(result?.data)
                    ? result.data
                    : [];

            if (formattedData.length === 0) {
                message.warning("No data found in selected date range");
            }
            setData(formattedData);
            const total = formattedData[0]?.totalRows || 0;
            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));

            setFilteredData(formattedData);


        } catch (err) {
            message.error("Failed to load billing report data.");
        } finally {
            setLoading(false);
        }
    };


    if (loading || columnsLoading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Billing Report</h4>
                </div>
            </div>
            <div className="advance-filter-container">
                <div className="advance-filter-title-container">
                    <div className="advance-filter-title">
                        <span>
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M14.7245 3.89258V4.35423C14.7245 5.04879 14.7245 5.39607 14.5508 5.68396C14.3771 5.97184 14.0597 6.15052 13.4249 6.50787L11.4754 7.6053C11.0494 7.84506 10.8365 7.96494 10.684 8.09732C10.3665 8.37299 10.171 8.69691 10.0824 9.0942C10.0399 9.28498 10.0399 9.50821 10.0399 9.95468V11.7412C10.0399 13.0191 10.0399 13.658 9.59287 13.9376C9.14585 14.2172 8.51723 13.9714 7.25999 13.48C6.66111 13.2458 6.36167 13.1288 6.19308 12.8915C6.02448 12.6542 6.02448 12.3499 6.02448 11.7412L6.02448 9.95468C6.02448 9.50821 6.02448 9.28498 5.98194 9.0942C5.89336 8.69691 5.69788 8.37299 5.38035 8.09732C5.22787 7.96494 5.01491 7.84506 4.589 7.6053L2.63951 6.50787C2.00471 6.15052 1.68731 5.97184 1.51358 5.68396C1.33984 5.39607 1.33984 5.04879 1.33984 4.35423V3.89258"
                                    stroke="#0009E2"
                                    strokeWidth="1.2"
                                />
                                <path
                                    opacity="0.5"
                                    d="M14.7245 3.89148C14.7245 3.00351 14.7245 2.55953 14.4305 2.28367C14.1365 2.00781 13.6633 2.00781 12.7168 2.00781H3.34754C2.40111 2.00781 1.92789 2.00781 1.63386 2.28367C1.33984 2.55953 1.33984 3.00351 1.33984 3.89148"
                                    stroke="#009DDB"
                                    strokeWidth="1.2"
                                />
                            </svg></span>Select Date Range</div>
                    <div className="advance-filter-action">
                        <button style={{ display: "none" }} className="active-page-header-button">
                            <span>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        opacity="0.5"
                                        cx="11.5"
                                        cy="11.5"
                                        r="7.5"
                                        stroke="currentColor"
                                        strokeWidth="1.35"
                                    />
                                    <path
                                        d="M15 12H8"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                        <button style={{ display: "none" }} className="active-page-header-button">
                            <span>
                                <svg
                                    width="25"
                                    height="25"
                                    viewBox="0 0 25 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12.0311"
                                        cy="11.9999"
                                        r="7.51943"
                                        stroke="#E93548"
                                    />
                                    <path
                                        d="M13.912 10.1192L10.1523 13.8789M10.1523 10.1191L13.912 13.8788"
                                        stroke="#E93548"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                    <div className="date-range-info-label" style={{ paddingBottom: "6px", whiteSpace: "nowrap" }}>
                        Note : Viewing active production data since <strong>{dayjs().subtract(90, "day").format("MM/DD/YYYY")}</strong>
                    </div>
                </div>
                <div className="advance-filter-body-container">

                    <div className="advance-filter-input-container">

                        <div className="advance-filter-input-width">
                            <div className="field-group">

                                <DatePicker variant="underlined" id="calendar-start-idle-id" placeholder=""
                                    className={`${"book-page-form-input-text"} ${selectedStartDate === null ? "" : "has-text"}`}
                                    suffixIcon={<CalendarIcon />}
                                    value={selectedStartDate}
                                    onChange={handleStartDateChange}
                                    disabledDate={disableFutureDates}
                                    allowClear={false}
                                />
                                <label htmlFor="calendar-start-idle-id" className={startfloatLabel}>
                                    Start Date <span className="form-required">*</span>
                                </label>
                                {errors.startdate && (
                                    <span className="error-msg">{errors.startdate}</span>
                                )}
                                {errors.datevalidate && (
                                    <span className="error-msg-1">{errors.datevalidate}</span>
                                )}
                            </div>
                        </div>

                        <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end", gap: "16px", flex: 1 }}>
                            <div className="advance-filter-input-width" style={{ flex: "0 0 auto" }}>
                                <div className="field-group">
                                    <DatePicker variant="underlined" id="calendar-end-idle-id" placeholder=""
                                        className={`${"book-page-form-input-text"} ${selectedEndDate === null ? "" : "has-text"}`}
                                        suffixIcon={<CalendarIcon />}
                                        value={selectedEndDate}
                                        onChange={handleEndDateChange}
                                        disabledDate={disableFutureDates}
                                        allowClear={false}
                                    />
                                    <label htmlFor="calendar-end-idle-id" className={endfloatLabel}>
                                        End Date <span className="form-required">*</span>
                                    </label>
                                    {errors.enddate && (
                                        <span className="error-msg">{errors.enddate}</span>
                                    )}
                                </div>
                            </div>


                        </div>

                    </div>
                    <div className="advance-filter-button-master-container">
                        <div className="advance-filter-button-container">
                            <button className="cancel-button-gray-outline" onClick={handleCleardate}>Clear</button>
                            {/*<button class="submit-button-gradiant-outline" onClick={() => fetchData({ pagination, sorter: sortedInfo, filters: searchFilters })}>Generate</button>*/}
                            <button className="submit-button-gradiant-outline" data-track="billing-report-generate" onClick={(e) => {

                                e.preventDefault();
                                if (!validate()) {
                                    const firstErrorField = Object.keys(errors)[0];
                                    if (firstErrorField) {
                                        const element = document.getElementById(firstErrorField);
                                        if (element) element.focus();
                                    }
                                    return;
                                }

                                const resetPagination = {
                                    ...pagination,
                                    current: 1
                                };
                                setPagination(resetPagination);
                                fetchData({
                                    pagination: resetPagination,
                                    sorter: sortedInfo,
                                    filters: searchFilters
                                });
                            }}>Generate</button>
                        </div>
                        <button
                            className="submit-button-gradiant-outline"
                            data-track="reports-billing-export-csv"
                            onClick={(e) => {
                                e.preventDefault();
                                if (!validate()) {
                                    const firstErrorField =
                                        Object.keys(errors)[0];
                                    if (firstErrorField) {
                                        const element =
                                            document.getElementById(
                                                firstErrorField
                                            );
                                        if (element) element.focus();
                                    }
                                    return;
                                }
                                handleExport_new();
                            }}
                        >
                            Export as CSV
                        </button>
                        <CSVLink
                            data={filteredData}
                            headers={activeColumns
                                .filter((col) => col && col.title && col.dataIndex)
                                .map((col) => ({
                                    label: col.title,
                                    key: col.dataIndex,
                                }))}
                            filename={csvFileName}
                            style={{ display: "none" }}
                            ref={csvLinkRef}
                        />
                    </div>
                </div>
            </div>
            <div className="active-page-body-container">
                <Table

                    columns={activeColumns}
                    dataSource={data}
                    className="common-pagination common-grid"
                    pagination={{
                        ...pagination,
                        pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                        showSizeChanger: true,
                        position: ["bottomRight"],
                        defaultPageSize: 10,
                        showTotal: (total, range) =>
                            renderCommonPaginationTotal(total, range, {
                                pagination,
                                handleTableChange,
                                searchFilters,
                                sortedInfo
                            }),

                    }}
                    rowKey={(record) => record.bid}
                    onChange={handleTableChange}
                    scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                    rowClassName={getRowClassName}
                />
            </div>

        </div>
    );
};
export default BillingReport;